import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileCode, Search, Users, AlertCircle, TrendingUp, Sparkles } from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Card } from '@/components/ui/card';
import { EarlyAccessForm } from '@/components/EarlyAccessForm';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const ANALYZED = [
  {
    icon: FileCode,
    title: 'Template inventory',
    detail:
      'Complete catalog of every WFR template in your project library — supplier coverage, template type (header vs line vs structured), field definitions, sample-document references.',
  },
  {
    icon: Users,
    title: 'Author + last-modified analysis',
    detail:
      'Who created each template, when it was last modified, who maintained it. Identifies the skill-concentration risk and the ownership history.',
  },
  {
    icon: Search,
    title: 'Field coverage matrix',
    detail:
      'Which fields each template extracts, confidence-threshold settings, validation rules. Surfaces inconsistencies across templates that look similar but extract differently.',
  },
  {
    icon: AlertCircle,
    title: 'Supplier-master cross-reference',
    detail:
      'Cross-references against your EBS or Fusion supplier master to identify orphaned templates (suppliers no longer active) and missing templates (active suppliers without coverage).',
  },
  {
    icon: TrendingUp,
    title: 'Verifier-queue load prediction',
    detail:
      'Per-template estimate of verifier review burden — combining template confidence settings, field complexity, and likely accuracy. Surfaces where AI extraction would deliver the most operational lift.',
  },
  {
    icon: Sparkles,
    title: 'Migration-readiness scoring',
    detail:
      'Per-template scoring on how cleanly the encoded business knowledge transfers forward into modern AI extraction. Identifies which templates are straightforward to migrate and which have complex logic worth preserving.',
  },
];

const HOW_IT_WORKS = [
  {
    step: '1',
    title: 'Request early access',
    body: 'Sign up below. Andrew personally emails you within 1 business day to confirm fit and walk you through the export process.',
  },
  {
    step: '2',
    title: 'Export your WFR projects',
    body: 'Standard WFR export — your team produces .fwr / .par project files for the templates you want analyzed. No vendor environment access required.',
  },
  {
    step: '3',
    title: 'Upload the export to the analyzer',
    body: 'Secure web upload via our analyzer. Files are processed and results delivered within 24 hours. No data retention beyond the analysis window.',
  },
  {
    step: '4',
    title: 'Receive your analysis report',
    body: 'A clean PDF report covering inventory, coverage, supplier-master cross-reference, verifier-queue prediction, and migration-readiness scoring. Yours to keep.',
  },
];

const FAQS = [
  {
    q: 'What WFR file formats does the analyzer support?',
    a: 'Standard WFR project exports — .fwr and .par files across WFR versions 11g, 12c, and 14c. The analyzer reads the project XML, template definitions, and supplier-mapping metadata.',
  },
  {
    q: 'What does it cost?',
    a: 'Free for the duration of the December 2026 decision window. The Analyzer is part of EZ Cloud\'s WebCenter modernization outreach. No license fee, no per-template cost, no expiry.',
  },
  {
    q: 'Why early access? Why not just upload immediately?',
    a: 'WFR analysis benefits from a personal walkthrough — Andrew calibrates the analysis to your specific implementation and stack version before processing. After the early-access cohort is established, self-serve upload follows.',
  },
  {
    q: 'Is my WFR project data sent anywhere or retained?',
    a: 'Files are processed once and the analysis report is generated. Files are deleted after analysis. No data is retained beyond the analysis window. No data is shared with third parties.',
  },
  {
    q: 'Who built this?',
    a: `${FOUNDER.name}, founder of EZ Cloud and a 25-year Oracle WebCenter AP specialist with deep WFR experience across hundreds of templates. EZ Cloud is published on the Oracle Reference Architecture for OCI and featured in Oracle's own technical case studies.`,
  },
];

const WfrTemplateAnalyzer = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="WFR Template Analyzer — Oracle WebCenter Forms Recognition Audit Tool | EZ Cloud"
        description="A free analysis tool for Oracle WebCenter Forms Recognition (WFR) customers. Upload your WFR project export and receive a structured report covering template inventory, supplier coverage, verifier-queue load prediction, and migration-readiness scoring. Early access for selected WebCenter customers."
        canonical="https://ezcloud.ai/resources/wfr-template-analyzer"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Resources', url: 'https://ezcloud.ai/resources/wfr-template-analyzer' },
          {
            name: 'WFR Template Analyzer',
            url: 'https://ezcloud.ai/resources/wfr-template-analyzer',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <main>
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <FileCode className="w-4 h-4" aria-hidden />
              Free Tool · Early Access
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              The WFR Template Analyzer
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Upload your Oracle WebCenter Forms Recognition project export — get back a
              structured analysis of every template, every supplier, every field, every business
              rule.
            </p>
            <p className="text-lg text-white/70 max-w-3xl">
              Discover what you actually have running, where the maintenance burden lives, and how
              cleanly each template's business knowledge transfers forward in a modernization.
            </p>
          </div>
        </section>

        <section className="py-12 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-3xl">
            <EarlyAccessForm
              tool="WFR Template Analyzer"
              source="wfr-analyzer-top"
              eventName="wfr_analyzer_early_access"
              buttonLabel="Request early access"
            />
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What the Analyzer produces
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Six dimensions of WFR analysis
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The Analyzer is purpose-built for WebCenter Forms Recognition customers facing the
              December 2026 Fusion Middleware 12c support cliff. Every output dimension supports
              the modernization decision.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {ANALYZED.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <h3 className="text-gray-900 font-semibold mb-2">{title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How it works
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              From WFR export to printable analysis report
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The Analyzer uses standard WFR project exports. No vendor environment access. No
              data retention beyond the analysis window.
            </p>
            <div className="space-y-5">
              {HOW_IT_WORKS.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <Card className="p-8 md:p-10 bg-white border border-gray-200 shadow-md">
              <p className="text-xs font-bold uppercase tracking-wide text-accent mb-3">
                Why this matters
              </p>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Your WFR template library represents years of business knowledge
              </h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Most WebCenter Forms Recognition implementations have accumulated templates over
                  a decade — sometimes hundreds of them. Each template encodes specific business
                  knowledge: which supplier sends which invoice format, where the fields appear,
                  what validation rules apply, which exceptions route where.
                </p>
                <p>
                  That business knowledge is valuable. It transfers forward into a modern AI
                  extraction model as initial training context — accelerating day-one accuracy on
                  modernization. But only if you know what you have, where the gaps are, and which
                  templates carry the most operational lift.
                </p>
                <p>
                  The Analyzer surfaces all of it. The output is a report you can use internally
                  — for budget conversations, for modernization planning, for procurement, for
                  Oracle account-team discussions. Useful whichever path you choose.
                </p>
                <p>
                  <Link
                    to="/solutions/modernize-webcenter-forms-recognition"
                    className="font-semibold text-primary hover:text-accent transition-colors"
                  >
                    Read the WFR modernization framework
                  </Link>{' '}
                  for the strategic context the Analyzer's output supports.
                </p>
              </div>
            </Card>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers about the Analyzer
            </h2>
            <div className="space-y-5">
              {FAQS.map((f) => (
                <Card key={f.q} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{f.q}</h3>
                  <p className="text-gray-700 leading-relaxed">{f.a}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              Ready to analyze your WFR template library?
            </h2>
            <p className="text-xl text-white/80 text-center mb-10">
              Request early access. {FOUNDER.name} will personally email you within 1 business day.
            </p>
            <EarlyAccessForm
              tool="WFR Template Analyzer"
              source="wfr-analyzer-bottom"
              eventName="wfr_analyzer_early_access"
              buttonLabel="Request early access"
            />
          </div>
        </section>
      </main>
    </div>
  );
};

export default WfrTemplateAnalyzer;
